import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, getResponsiveThumbnailUrl } from '@/lib/utils';
import { Users, Clock, TrendingUp, Gavel } from 'lucide-react';
import { listingsAPI } from '@/lib/api';
import { Link } from 'react-router-dom';

const DashboardParticipatingTab = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchParticipating = async () => {
            try {
                const res = await listingsAPI.getMyBids();
                setAuctions(res.data || []);
            } catch (error) {
                console.error('Error fetching participating auctions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchParticipating();
    }, []);

    const getTimeRemaining = (endDate) => {
        if (!endDate) return 'N/A';
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) return 'Încheiată';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}z ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                    <Gavel className="w-5 h-5 text-primary" />
                    Licitații
                </CardTitle>
                <CardDescription>
                    {auctions.length} licitații la care participi
                </CardDescription>
            </CardHeader>
            <CardContent>
                {auctions.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <Gavel className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Nu participi la nicio licitație momentan</p>
                        <p className="text-sm mt-2">Plasează o ofertă pentru a participa la o licitație</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {auctions.map((auction) => (
                            <Link 
                                key={auction.listing_id} 
                                to={`/listing/${auction.slug || auction.listing_id}`}
                                className="block"
                            >
                                <Card className={`hover:shadow-md transition-all cursor-pointer h-full ${
                                    auction.is_winning ? 'border-green-300 bg-green-50/30' : 
                                    auction.is_outbid ? 'border-orange-300 bg-orange-50/30' : ''
                                }`}>
                                    <CardContent className="p-3">
                                        <div className="aspect-square bg-white rounded-lg mb-3 overflow-hidden">
                                            {auction.images?.[0] ? (
                                                <img
                                                    src={getResponsiveThumbnailUrl(auction.images[0], 400, 200, 75)}
                                                    alt={auction.title}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Gavel className="w-8 h-8 text-gray-300" />
                                                </div>
                                            )}
                                        </div>
                                        <h3 className="font-medium text-sm truncate">{auction.title}</h3>
                                        <div className="mt-2 space-y-1">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-muted-foreground">Preț curent:</span>
                                                <span className="font-semibold text-primary">
                                                    {formatCurrency(auction.current_price)}
                                                </span>
                                            </div>
                                            {(auction.my_bid_amount || auction.user_max_bid || auction.my_max_bid) && (
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-muted-foreground">Oferta ta:</span>
                                                    <span className="font-medium">
                                                        {formatCurrency(auction.my_bid_amount || auction.user_max_bid || auction.my_max_bid)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                            {auction.is_winning ? (
                                                <Badge className="bg-green-100 text-green-800 text-xs">
                                                    <TrendingUp className="w-3 h-3 mr-1" />
                                                    Câștigi
                                                </Badge>
                                            ) : auction.is_outbid ? (
                                                <Badge className="bg-orange-100 text-orange-800 text-xs">
                                                    Depășit
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-xs">
                                                    În curs
                                                </Badge>
                                            )}
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="w-3 h-3" />
                                                {getTimeRemaining(auction.auction_end_date)}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default DashboardParticipatingTab;
